import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGrid, Button, Checkbox, DropdownButton, Flex, Grid, Text } from "@jsq/ds/core";
import { Form, FormTextInput, useFormContext } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useDebouncedValue } from "~/common/hooks/useDebouncedValue";
import { ComponentKind } from "~/graphql/supergraphEnums";
import { HighlightText } from "~/pages/Reporting/components/HighlightText";
import { makeHighlightRenderer } from "~/pages/Reporting/components/HighlightText/makeHighlightRenderer";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { SPACING } from "~/styles/variables";
import { toSentenceCaseLabel } from "../../shared/sentenceCaseLabel";
import { toPrimaryFieldOption } from "./primaryFieldOptions";
import * as styles from "./styles_vanilla.css";
import {
  PRIMARY_FIELDS_PAGE_SIZE,
  usePrimaryFieldsDatasource,
  usePrimaryFieldsGridAttachment,
} from "./usePrimaryFieldsDatasource";
import type { ComponentInsertion } from "./formulaComponents";
import type { Formula } from "./FormulasGrid";
import type { FlattenedNode, HierarchyNodeData, TypeHierarchy } from "./HierarchyTreeGrid";
import type { PrimaryFieldOption } from "./primaryFieldOptions";

type FieldSelectorProps = {
  typeHierarchy: TypeHierarchy | null;
  formulas: Formula[];
  /** Arena scope for the paginated primary-field fetch. `null` for
   *  system-level primary fields. */
  arenaId: number | null;
  onInsertField: (insertion: ComponentInsertion) => void;
  onClose: () => void;
};

type FilterType = "hierarchy" | "formulas" | "primaryFields";

type FieldSelectorFormValues = {
  search: string;
  filters: FilterType[];
};

const FILTER_OPTIONS: { value: FilterType; label: string }[] = [
  { value: "hierarchy", label: "Hierarchy nodes" },
  { value: "formulas", label: "Formulas" },
  { value: "primaryFields", label: "Primary fields" },
];

const INSERTED_DISPLAY_DURATION_MS = 500;

const FIELD_SELECTOR_INITIAL_VALUES: FieldSelectorFormValues = {
  search: "",
  filters: [],
};

const NOOP = () => {};

const FIELD_SELECTOR_GRID_OVERRIDES = {
  domLayout: "autoHeight" as const,
  rowHeight: 50,
  suppressContextMenu: true,
  suppressCellFocus: true,
  suppressHorizontalScroll: true,
  pagination: false,
  sideBar: false,
  statusBar: undefined,
};

const flattenHierarchy = (
  node: HierarchyNodeData,
  parentPath: string[] = [],
  parentNamePath: string[] = [],
): FlattenedNode[] => {
  const currentPath = [...parentPath, node.code];
  const currentNode: FlattenedNode = {
    id: currentPath.join("/"),
    name: node.name,
    code: node.code,
    description: node.description,
    path: currentPath,
    nodeId: node.id,
    namePath: parentNamePath,
  };
  const childNamePath = [...parentNamePath, node.name];
  const childNodes =
    node.children?.flatMap((child) => flattenHierarchy(child, currentPath, childNamePath)) ??
    [];
  return [currentNode, ...childNodes];
};

const nodeMatchesSearch = (node: FlattenedNode, searchTerm: string): boolean => {
  const lower = searchTerm.toLowerCase();
  return node.name.toLowerCase().includes(lower) || node.code.toLowerCase().includes(lower);
};

const filterNodesWithAncestors = (
  nodes: FlattenedNode[],
  searchTerm: string,
): FlattenedNode[] => {
  if (!searchTerm.trim()) return nodes;

  const matchingNodes = nodes.filter((node) => nodeMatchesSearch(node, searchTerm));
  const pathsToInclude = new Set<string>();
  matchingNodes.forEach((node) => {
    for (let i = 1; i <= node.path.length; i++) {
      pathsToInclude.add(node.path.slice(0, i).join("/"));
    }
  });
  return nodes.filter((node) => pathsToInclude.has(node.id));
};

const formulaMatchesSearch = (formula: Formula, searchTerm: string): boolean => {
  const lower = searchTerm.toLowerCase();
  return (
    formula.name.toLowerCase().includes(lower) ||
    formula.description.toLowerCase().includes(lower) ||
    formula.formula.toLowerCase().includes(lower)
  );
};

const hierarchyInsertKey = (rowId: string) => `hierarchy:${rowId}`;
const formulaInsertKey = (rowId: string) => `formula:${rowId}`;
const primaryFieldInsertKey = (rowId: string) => `primary:${rowId}`;

function useInsertFieldFeedback(onInsertField: (insertion: ComponentInsertion) => void) {
  const [insertedIds, setInsertedIds] = useState<Set<string>>(new Set());
  const insertedTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const insertGuardRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const timeouts = insertedTimeoutsRef.current;
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, []);

  const markInserted = useCallback((insertKey: string) => {
    setInsertedIds((prev) => new Set(prev).add(insertKey));
    const existing = insertedTimeoutsRef.current.get(insertKey);
    if (existing) clearTimeout(existing);

    insertedTimeoutsRef.current.set(
      insertKey,
      setTimeout(() => {
        setInsertedIds((prev) => {
          const next = new Set(prev);
          next.delete(insertKey);
          return next;
        });
        insertedTimeoutsRef.current.delete(insertKey);
        insertGuardRef.current.delete(insertKey);
      }, INSERTED_DISPLAY_DURATION_MS),
    );
  }, []);

  const handleInsertHierarchyField = useCallback(
    (insertKey: string, node: FlattenedNode) => {
      if (node.nodeId == null) return;
      if (insertGuardRef.current.has(insertKey)) return;
      insertGuardRef.current.add(insertKey);
      onInsertField({
        kind: ComponentKind.Node,
        nodeId: node.nodeId,
        targetName: node.name,
        targetPath: node.namePath,
      });
      markInserted(insertKey);
    },
    [onInsertField, markInserted],
  );

  const handleInsertFormulaField = useCallback(
    (insertKey: string, formula: Formula) => {
      if (insertGuardRef.current.has(insertKey)) return;
      insertGuardRef.current.add(insertKey);
      onInsertField({
        kind: ComponentKind.Formula,
        formulaId: Number(formula.id),
        targetName: formula.name,
      });
      markInserted(insertKey);
    },
    [onInsertField, markInserted],
  );

  const handleInsertPrimaryField = useCallback(
    (insertKey: string, field: PrimaryFieldOption) => {
      if (insertGuardRef.current.has(insertKey)) return;
      insertGuardRef.current.add(insertKey);
      onInsertField({
        kind: ComponentKind.PrimaryField,
        primaryField: field.primaryField,
        targetName: field.primaryField,
      });
      markInserted(insertKey);
    },
    [onInsertField, markInserted],
  );

  return {
    insertedIds,
    handleInsertHierarchyField,
    handleInsertFormulaField,
    handleInsertPrimaryField,
  };
}

type InsertColumnContext<TRow> = {
  insertedIds: Set<string>;
  onInsert: (insertKey: string, row: TRow) => void;
  /** Passed through context so highlight renderers pick up the current
   *  search without re-creating column defs on every keystroke. */
  searchValue: string;
};

const InsertButtonCellRenderer = ({
  isInserted,
  onInsertClick,
}: {
  isInserted: boolean;
  onInsertClick: () => void;
}) => {
  if (isInserted) {
    return (
      <Button
        variant="link"
        className={styles.insertedButton}
        icon="check"
        iconPosition="right"
      >
        Inserted
      </Button>
    );
  }
  return (
    <Button variant="link" onClick={onInsertClick} className={styles.insertButton} icon="plus">
      Insert
    </Button>
  );
};

const HIERARCHY_COLUMN_DEFS: AgGrid.ColDef<FlattenedNode>[] = [
  {
    colId: "description",
    headerName: "Description",
    sortable: false,
    flex: 2,
    cellRenderer: makeHighlightRenderer<FlattenedNode>((row) => row.description, {
      placeholder: "-",
    }),
  },
  {
    colId: "insert",
    headerName: "",
    width: 100,
    sortable: false,
    suppressHeaderMenuButton: true,
    cellClass: styles.insertColumnCell,
    cellRenderer: (params: AgGrid.ICellRendererParams<FlattenedNode>) => {
      // Unpersisted nodes have no id to reference, so they get no
      // insert affordance rather than a button that silently no-ops.
      if (!params.data || params.data.nodeId == null) return null;
      const ctx = params.context as InsertColumnContext<FlattenedNode>;
      const key = hierarchyInsertKey(params.data.id);
      return (
        <InsertButtonCellRenderer
          isInserted={ctx.insertedIds.has(key)}
          onInsertClick={() => ctx.onInsert(key, params.data!)}
        />
      );
    },
  },
];

const FORMULA_COLUMN_DEFS: AgGrid.ColDef<Formula>[] = [
  {
    colId: "name",
    headerName: "Formula name",
    sortable: false,
    flex: 1,
    cellRenderer: makeHighlightRenderer<Formula>((row) => row.name),
  },
  {
    colId: "description",
    headerName: "Description",
    sortable: false,
    flex: 1.5,
    cellRenderer: makeHighlightRenderer<Formula>((row) => row.description),
  },
  {
    colId: "formula",
    headerName: "Formula",
    sortable: false,
    flex: 2,
    wrapText: true,
    autoHeight: true,
    cellRenderer: makeHighlightRenderer<Formula>((row) => row.formula),
  },
  {
    colId: "insert",
    headerName: "",
    width: 100,
    sortable: false,
    suppressHeaderMenuButton: true,
    cellClass: styles.insertColumnCell,
    cellRenderer: (params: AgGrid.ICellRendererParams<Formula>) => {
      if (!params.data) return null;
      const ctx = params.context as InsertColumnContext<Formula>;
      const key = formulaInsertKey(String(params.data.id));
      return (
        <InsertButtonCellRenderer
          isInserted={ctx.insertedIds.has(key)}
          onInsertClick={() => ctx.onInsert(key, params.data!)}
        />
      );
    },
  },
];

const PRIMARY_FIELD_COLUMN_DEFS: AgGrid.ColDef<PrimaryFieldOption>[] = [
  {
    colId: "name",
    headerName: "Field name",
    sortable: false,
    flex: 1,
    cellRenderer: makeHighlightRenderer<PrimaryFieldOption>((row) =>
      toSentenceCaseLabel(row.name),
    ),
  },
  {
    colId: "fieldKey",
    headerName: "Field key",
    sortable: false,
    flex: 1,
    cellRenderer: makeHighlightRenderer<PrimaryFieldOption>((row) =>
      row.tableName ? toSentenceCaseLabel(row.tableName) : "",
    ),
  },
  { field: "type", headerName: "Type", sortable: false, flex: 1 },
  {
    colId: "insert",
    headerName: "",
    width: 100,
    sortable: false,
    suppressHeaderMenuButton: true,
    cellClass: styles.insertColumnCell,
    cellRenderer: (params: AgGrid.ICellRendererParams<PrimaryFieldOption>) => {
      if (!params.data) return null;
      const ctx = params.context as InsertColumnContext<PrimaryFieldOption>;
      const key = primaryFieldInsertKey(params.data.primaryField);
      return (
        <InsertButtonCellRenderer
          isInserted={ctx.insertedIds.has(key)}
          onInsertClick={() => ctx.onInsert(key, params.data!)}
        />
      );
    },
  },
];

export const FieldSelector = (props: FieldSelectorProps) => (
  <Form<FieldSelectorFormValues> initialValues={FIELD_SELECTOR_INITIAL_VALUES} onSubmit={NOOP}>
    <FieldSelectorContent {...props} />
  </Form>
);

const FieldSelectorContent = ({
  typeHierarchy,
  formulas,
  arenaId,
  onInsertField,
  onClose,
}: FieldSelectorProps) => {
  const { values, setFieldValue } = useFormContext<FieldSelectorFormValues>();
  const searchValue = values.search;
  const activeFilters = values.filters;
  const debouncedSearch = useDebouncedValue(searchValue, 150);
  const primaryFieldSearch = debouncedSearch.trim() || null;

  const { datasource: primaryFieldsDatasource, loadState: primaryFieldsLoadState } =
    usePrimaryFieldsDatasource({
      arenaId,
      search: primaryFieldSearch,
      toRow: toPrimaryFieldOption,
    });

  const {
    insertedIds,
    handleInsertHierarchyField,
    handleInsertFormulaField,
    handleInsertPrimaryField,
  } = useInsertFieldFeedback(onInsertField);

  const showSection = useCallback(
    (section: FilterType) => activeFilters.length === 0 || activeFilters.includes(section),
    [activeFilters],
  );

  const toggleFilter = useCallback(
    (filter: FilterType) => {
      const next = activeFilters.includes(filter)
        ? activeFilters.filter((f) => f !== filter)
        : [...activeFilters, filter];
      void setFieldValue("filters", next);
    },
    [activeFilters, setFieldValue],
  );

  const filterLabel = useMemo(() => {
    if (activeFilters.length === 0 || activeFilters.length === FILTER_OPTIONS.length) {
      return "All field types";
    }
    if (activeFilters.length === 1) {
      const selected = FILTER_OPTIONS.find((o) => o.value === activeFilters[0]);
      return `${selected?.label ?? activeFilters[0]} only`;
    }
    return `${activeFilters.length} selected`;
  }, [activeFilters]);

  const allHierarchyNodes = useMemo<FlattenedNode[]>(() => {
    if (!typeHierarchy) return [];
    const root = typeHierarchy.tree_structure?.root;
    if (!root) return [];
    return flattenHierarchy(root);
  }, [typeHierarchy]);

  const filteredHierarchyNodes = useMemo(
    () => filterNodesWithAncestors(allHierarchyNodes, searchValue),
    [allHierarchyNodes, searchValue],
  );

  const filteredFormulas = useMemo(() => {
    if (!searchValue.trim()) return formulas;
    return formulas.filter((f) => formulaMatchesSearch(f, searchValue));
  }, [formulas, searchValue]);

  const hierarchyGroupDefaultExpanded = useMemo(
    () => (searchValue.trim() ? -1 : 1),
    [searchValue],
  );

  const hierarchyGridApiRef = useRef<AgGrid.GridApi<FlattenedNode> | null>(null);
  const formulaGridApiRef = useRef<AgGrid.GridApi<Formula> | null>(null);
  const { gridApiRef: primaryFieldGridApiRef, onGridReady: handlePrimaryFieldGridReady } =
    usePrimaryFieldsGridAttachment<PrimaryFieldOption>(primaryFieldsDatasource);

  const hierarchyContext = useMemo<InsertColumnContext<FlattenedNode>>(
    () => ({ insertedIds, onInsert: handleInsertHierarchyField, searchValue }),
    [insertedIds, handleInsertHierarchyField, searchValue],
  );

  const formulaContext = useMemo<InsertColumnContext<Formula>>(
    () => ({ insertedIds, onInsert: handleInsertFormulaField, searchValue }),
    [insertedIds, handleInsertFormulaField, searchValue],
  );

  const primaryFieldContext = useMemo<InsertColumnContext<PrimaryFieldOption>>(
    () => ({ insertedIds, onInsert: handleInsertPrimaryField, searchValue }),
    [insertedIds, handleInsertPrimaryField, searchValue],
  );

  useEffect(() => {
    hierarchyGridApiRef.current?.refreshCells({ columns: ["insert"] });
    formulaGridApiRef.current?.refreshCells({ columns: ["insert"] });
    primaryFieldGridApiRef.current?.refreshCells({ columns: ["insert"], force: true });
  }, [insertedIds, primaryFieldGridApiRef]);

  /** Refresh text cells when the search changes so cached renderers pick
   *  up updated highlights. */
  useEffect(() => {
    hierarchyGridApiRef.current?.refreshCells({
      columns: ["ag-Grid-AutoColumn", "description"],
      force: true,
    });
    formulaGridApiRef.current?.refreshCells({
      columns: ["name", "description", "formula"],
      force: true,
    });
    primaryFieldGridApiRef.current?.refreshCells({
      columns: ["name", "fieldKey"],
      force: true,
    });
  }, [searchValue, primaryFieldGridApiRef]);

  const handleHierarchyGridReady = useCallback(
    (event: AgGrid.GridReadyEvent<FlattenedNode>) => {
      hierarchyGridApiRef.current = event.api;
    },
    [],
  );

  const handleFormulaGridReady = useCallback((event: AgGrid.GridReadyEvent<Formula>) => {
    formulaGridApiRef.current = event.api;
  }, []);

  const hierarchyAutoGroupColumnDef = useMemo<AgGrid.ColDef<FlattenedNode>>(
    () => ({
      headerName: "Node name",
      field: "name",
      sortable: false,
      flex: 1,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: (params: AgGrid.ICellRendererParams<FlattenedNode>) => {
          const search = (params.context as { searchValue?: string })?.searchValue ?? "";
          return <HighlightText text={params.data?.name ?? ""} searchValue={search} />;
        },
      },
    }),
    [],
  );

  const getDataPath = useCallback((data: FlattenedNode): string[] => data.path, []);

  const { gridOptions: hierarchyGridOptions } = useAgGridDefaults({
    overrides: FIELD_SELECTOR_GRID_OVERRIDES,
  });

  const { gridOptions: formulaGridOptions } = useAgGridDefaults({
    overrides: FIELD_SELECTOR_GRID_OVERRIDES,
  });

  const { gridOptions: primaryFieldGridOptions } = useAgGridDefaults({
    overrides: {
      ...FIELD_SELECTOR_GRID_OVERRIDES,
      rowModelType: "serverSide",
      pagination: true,
      paginationPageSize: PRIMARY_FIELDS_PAGE_SIZE,
      paginationPageSizeSelector: false,
      cacheBlockSize: PRIMARY_FIELDS_PAGE_SIZE,
      suppressServerSideFullWidthLoadingRow: true,
    },
  });

  const isSearchActive = searchValue.trim().length > 0;
  const primaryFieldsSearchLoaded =
    primaryFieldsLoadState.search === primaryFieldSearch &&
    primaryFieldsLoadState.hasLoaded &&
    !primaryFieldsLoadState.isLoading;
  const primaryFieldsLoadFailed = primaryFieldsSearchLoaded && primaryFieldsLoadState.hasError;
  const primaryFieldsHaveNoRows =
    primaryFieldsSearchLoaded &&
    !primaryFieldsLoadState.hasError &&
    primaryFieldsLoadState.rowCount === 0;
  const hasPrimaryFieldSearchResults =
    !showSection("primaryFields") ||
    !primaryFieldsSearchLoaded ||
    primaryFieldsLoadFailed ||
    primaryFieldsLoadState.rowCount > 0;
  const hasNoSearchResults = useMemo(() => {
    if (!isSearchActive) return false;
    const hierarchyEmpty = !showSection("hierarchy") || filteredHierarchyNodes.length === 0;
    const formulasEmpty = !showSection("formulas") || filteredFormulas.length === 0;
    return hierarchyEmpty && formulasEmpty && !hasPrimaryFieldSearchResults;
  }, [
    isSearchActive,
    showSection,
    filteredHierarchyNodes.length,
    filteredFormulas.length,
    hasPrimaryFieldSearchResults,
  ]);

  return (
    <Flex flexDirection="column" gap={SPACING.X_LARGE}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text variant="h3">Insert fields</Text>
        <Button variant="link" onClick={onClose} icon="arrow-right" iconPosition="right">
          Close
        </Button>
      </Flex>

      <Flex alignItems="center" gap={SPACING.MEDIUM}>
        <FormTextInput
          name="search"
          icon="search"
          isClearable
          placeholder="Search available fields"
        />
        <DropdownButton
          label={filterLabel}
          keepOpen
          variant="neutral"
          icon="filter"
          iconPosition="left"
        >
          {FILTER_OPTIONS.map((option) => (
            <DropdownButton.Action
              key={option.value}
              onSelect={() => toggleFilter(option.value)}
            >
              <Checkbox
                checked={activeFilters.includes(option.value)}
                onChange={() => {
                  /* handled by onSelect */
                }}
                label={option.label}
              />
            </DropdownButton.Action>
          ))}
        </DropdownButton>
      </Flex>

      {hasNoSearchResults ? (
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={SPACING.X_SMALL}
          className={styles.noResultsContainer}
          data-testid="field-selector-no-results"
        >
          <Icon type="search" size="xxx-large" color={Colors.Neutral600} />
          <Text variant="h4">No results found</Text>
          <Text variant="regular-sm" align="center">
            No match within the hierarchy, formulas, or primary fields. Try a different search
            term.
          </Text>
        </Flex>
      ) : (
        <>
          {showSection("hierarchy") && (
            <Flex flexDirection="column" gap={SPACING.MEDIUM}>
              <Text variant="h4">Hierarchy</Text>
              {filteredHierarchyNodes.length > 0 ? (
                <Grid<FlattenedNode>
                  rowData={filteredHierarchyNodes}
                  getRowId={(params) => params.data?.id ?? ""}
                  columnDefs={HIERARCHY_COLUMN_DEFS}
                  context={hierarchyContext}
                  onGridReady={handleHierarchyGridReady}
                  treeData
                  getDataPath={getDataPath}
                  autoGroupColumnDef={hierarchyAutoGroupColumnDef}
                  groupDefaultExpanded={hierarchyGroupDefaultExpanded}
                  gridOptions={hierarchyGridOptions}
                />
              ) : (
                <Text variant="secondary-sm">No hierarchy nodes available</Text>
              )}
            </Flex>
          )}

          {showSection("formulas") && (
            <Flex flexDirection="column" gap={SPACING.MEDIUM}>
              <Text variant="h4">Formulas</Text>
              {filteredFormulas.length > 0 ? (
                <Grid<Formula>
                  rowData={filteredFormulas}
                  getRowId={(params) => params.data?.id ?? ""}
                  columnDefs={FORMULA_COLUMN_DEFS}
                  context={formulaContext}
                  onGridReady={handleFormulaGridReady}
                  gridOptions={formulaGridOptions}
                />
              ) : (
                <Text variant="secondary-sm">No formulas available</Text>
              )}
            </Flex>
          )}

          {showSection("primaryFields") && (
            <Flex flexDirection="column" gap={SPACING.MEDIUM}>
              <Text variant="h4">Primary fields</Text>
              {primaryFieldsLoadFailed ? (
                <Text variant="secondary-sm">Unable to load primary fields</Text>
              ) : primaryFieldsHaveNoRows ? (
                <Text variant="secondary-sm">No primary fields available</Text>
              ) : (
                <Grid<PrimaryFieldOption>
                  getRowId={(params) => params.data?.primaryField ?? ""}
                  columnDefs={PRIMARY_FIELD_COLUMN_DEFS}
                  context={primaryFieldContext}
                  onGridReady={handlePrimaryFieldGridReady}
                  gridOptions={primaryFieldGridOptions}
                />
              )}
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};
